'use client';

import { segment } from '@hyphengroup/cms-components/modules/segment';
import {
  generateEmailHash,
  getFirstAndLastName,
} from '@hyphengroup/cms-components/utils/emailHash';
import { Comparable_data } from '__generated__/graphql';
import { sha256 } from 'js-sha256';
import { JsonObject } from 'utils/json';

export type TrackingData =
  | DataLayerObject
  | DataLayerObject[]
  | (() => DataLayerObject | DataLayerObject[]);

export type TrackDetailProps = {
  event: string;
  location: string;
};

export type ComponentType =
  | 'sidebar'
  | 'top-banner'
  | 'bottom-sticky'
  | 'hero-image'
  | 'survey'
  | 'top-nav'
  | 'pop-up'
  | 'above-footer'
  | 'middle'
  | 'footer'
  | 'tooltip'
  | 'exit-pop-up'
  | 'before-footer'
  | 'vertical-tiles';

export type SwitchClickTracking = {
  event: string;
  location: string;
  action: string;
  title: string;
  componentType?: ComponentType;
};

export type GeneralTracking = {
  location: string;
  componentType?: ComponentType;
  verticalCode?: string;
};

export function track(data: TrackingData): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    const object = typeof data === 'function' ? data() : data;
    const sendData = (data: DataLayerObject) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('dataLayer event', data);
      }
      window.dataLayer.push(data);

      // Send to Segment
      if (typeof data.event === 'string') {
        segment.track(data.event, data);
      }
    };

    if (Array.isArray(object)) {
      object.forEach(sendData);
    } else {
      sendData(object);
    }
  }
}

export function getSession(): JsonObject {
  const match = document.cookie.match(/CAG_Session=.+?\.(.+?)\..+?;/);
  return match ? JSON.parse(atob(match[1])) : {};
}

/** Account Button Events */
export function accountButtonHoverEvent(location?: string): JsonObject {
  return {
    event: 'navigate',
    location:
      window.hyphen?.meta.tracking?.location || location || 'results page',
    componentType: 'top-nav',
    action: 'view',
    title: 'profile-button-menu',
  };
}

export function comparablesMappingHandler(comparable: Comparable_data): string {
  const { prefix, displayValue, title } = comparable;
  return `${prefix}${displayValue} ${title.display}`;
}

export function trackAnchorClick(
  title: string,
  location: string,
  componentType?: ComponentType,
): void {
  const trackingData: SwitchClickTracking = {
    event: 'featureDetailClick',
    location,
    action: 'see more detail',
    title,
    componentType,
  };
  track(trackingData);
}

export function trackCardItemClick(
  event: string,
  location: string,
  componentType: ComponentType,
  action: string,
  title: string,
  position: number,
): void {
  track({
    event: event,
    location: location,
    componentType: componentType,
    action: action,
    title: title,
    position: position,
  });
}

export function subscribeClick(
  location: string,
  componentType: ComponentType,
): void {
  track({
    event: 'subscribeClick',
    componentType: componentType,
    location: location,
    action: 'click',
    title: 'submit',
  });
}

export function trackingAuthButtonClick(
  intent: string,
  loginMethod: string,
  componentTitle: string,
  marketingConsent?: boolean,
  componentType?: string,
  stepName?: string,
  eventName?: string,
  location?: string,
): void {
  return track({
    event: eventName ?? 'login_signup_interact',
    location: location ?? 'login page',
    intent: intent,
    login_method: loginMethod,
    step_number: '1',
    step_name: stepName ?? 'user-auth',
    action: 'click',
    component_type: componentType ?? 'button',
    component_title: componentTitle,
    marketing_consent:
      typeof marketingConsent !== 'undefined'
        ? marketingConsent
          ? 'yes'
          : 'no'
        : null,
  });
}

export type MembershipRewardsTrackingData = {
  campaignId?: number;
  campaignName?: string;
  email?: string;
  leadId?: number;
  fullName?: string;
  selectedReward?: string;
  contactNumber?: string;
  product?: {
    productUid?: string;
    productName?: string;
    providerName?: string;
  };
};

export function trackingMembershipRewards(
  intent: string,
  stepName: string,
  stepNumber: number,
  data: MembershipRewardsTrackingData,
  componentType?: string,
  componentTitle?: string,
): void {
  const userData = trackingUserData(
    data.email ?? '',
    data.fullName,
    data.contactNumber,
  );

  const items = [];
  if (data.product) {
    items.push({
      item_id: data.product.productUid,
      item_name: data.product.productName,
      item_brand: data.product.providerName,
    });
  }

  const trackingData: JsonObject = {
    event: 'reward_gateway',
    location: 'rewards page',
    step_number: stepNumber.toString(),
    step_name: stepName,
    intent: intent,
    lead_id: data.leadId,
    reward_campaign_name: data.campaignName,
    reward_campaign_id: data.campaignId,
    reward_selected: data.selectedReward ?? null,
    user_data: userData,
    userIdentities: {
      email: userData.email,
      other: userData.email_hash_md5,
    },
    items: items,
  };

  if (componentType) {
    trackingData.component_type = componentType;
  }

  if (componentTitle) {
    trackingData.component_title = componentTitle;
  }

  return track(trackingData);
}

export function clickActionMembershipRewards(
  intent: string,
  contentTitle: string,
  contentType: string,
  data: MembershipRewardsTrackingData,
): void {
  const userData = trackingUserData(
    data.email ?? '',
    data.fullName,
    data.contactNumber,
  );

  const items = [];
  if (data.product) {
    items.push({
      item_id: data.product.productUid,
      item_name: data.product.productName,
      item_brand: data.product.providerName,
    });
  }

  return track({
    event: 'reward_gateway',
    action: 'click',
    contentTitle: contentTitle,
    contentType: contentType,
    location: 'rewards page',
    intent: intent,
    lead_id: data.leadId,
    reward_campaign_name: data.campaignName,
    reward_campaign_id: data.campaignId,
    reward_selected: data.selectedReward ?? null,
    user_data: userData,
    userIdentities: {
      email: userData.email,
      other: userData.email_hash_md5,
    },
    items: items,
  });
}

function trackingUserData(
  email: string,
  fullname?: string,
  contactNumber?: string,
): JsonObject {
  const { firstName, lastName } = getFirstAndLastName(fullname);
  const userData: JsonObject = {
    email: email,
    first_name: firstName ?? null,
    last_name: lastName ?? null,
    phone_number: contactNumber ?? null,
  };

  Object.keys(userData).forEach(async (key) => {
    if (!userData[key]) return;
    if (key === 'email') {
      userData[`${key}_hash_md5`] = generateEmailHash(String(userData[key]));
      userData[`${key}_hash_sha256`] = sha256(
        String(userData[key]).toLowerCase(),
      );
      return;
    }
    userData[`${key}_sha256`] = sha256(String(userData[key])).toLowerCase();
  });

  const keys = Object.keys(userData);
  keys.forEach((key) => {
    const value = userData[key];
    if (
      value === null ||
      (typeof value === 'object' && Object.keys(value).length === 0)
    ) {
      delete userData[key];
    }
  });

  return userData;
}

export function trackLeadCaptureFormInteract(
  componentType: string,
  componentTitle: string,
  fieldValue: string,
  stepName: string,
  stepNumber: number,
  verticalCode: string,
  action?: string,
): void {
  return track({
    event: 'form_interact',
    lead_form_name: 'reward-lead-detail-form',
    form_type: 'reward form',
    action: action ?? 'fill',
    component_type: componentType,
    component_title: componentTitle,
    vertical_code: verticalCode,
    form_field_value: fieldValue ?? null,
    step_number: stepNumber.toString(),
    step_name: stepName,
    status: 'success',
  });
}

export function trackLeadCaptureClick(
  componentType: string,
  componentTitle: string,
  data: MembershipRewardsTrackingData,
  stepName: string,
  stepNumber: number,
  event?: string,
  intent?: string,
  action?: string,
): void {
  const items = [];
  if (data.product) {
    items.push({
      item_id: data.product.productUid,
      item_name: data.product.productName,
      item_brand: data.product.providerName,
    });
  }

  return track({
    event: event ?? 'reward_gateway',
    component_type: componentType,
    component_title: componentTitle,
    action: action ?? 'click',
    step_name: stepName,
    step_number: stepNumber.toString(),
    intent: intent ?? undefined,
    item_list_name: 'reward page',
    items: items,
    reward_campaign_name: data.campaignName,
    reward_campaign_id: data.campaignId,
  });
}

type TrackLeadCaptureItem = {
  item_id?: string;
  item_name?: string;
  item_brand?: string;
};

type TrackLeadCaptureAuthButton = {
  event: string;
  step_number: string;
  step_name: string;
  intent: string;
  login_method: string;
  marketing_consent?: string;
  component_type: string;
  component_title: string;
  items?: TrackLeadCaptureItem[];
  reward_campaign_name?: string;
  reward_campaign_id?: number;
  location?: string;
};

export function trackingLeadCaptureAuthButton(
  intent: string,
  loginMethod: string,
  componentType: string,
  componentTitle: string,
  data: MembershipRewardsTrackingData,
  marketingConsent?: boolean,
  stepName?: string,
  eventName?: string,
  location?: string,
): void {
  const trackObj: TrackLeadCaptureAuthButton = {
    event: eventName ?? 'login_signup_interact',
    intent: intent,
    login_method: loginMethod,
    step_number: '1',
    step_name: stepName ?? 'user-auth',
    component_type: componentType ?? 'button',
    component_title: componentTitle,
    marketing_consent:
      typeof marketingConsent !== 'undefined'
        ? marketingConsent
          ? 'yes'
          : 'no'
        : undefined,
    reward_campaign_id: data.campaignId,
    reward_campaign_name: data.campaignName,
  };

  if (data.product) {
    const items: TrackLeadCaptureItem[] = [];
    items.push({
      item_id: data.product.productUid,
      item_name: data.product.productName,
      item_brand: data.product.providerName,
    });
    trackObj.items = items;
  }

  if (location) {
    trackObj.location = location;
  }

  return track(trackObj);
}

export function normalizePhoneNumber(
  phoneNumber?: string,
  country?: string,
): string | undefined {
  if (!phoneNumber) {
    return undefined;
  }

  const normalizedPhoneNumber = phoneNumber.replace(/\D/g, '');
  if (
    country?.toLocaleLowerCase() === 'singsaver' &&
    !normalizedPhoneNumber.startsWith('65')
  ) {
    return `65${normalizedPhoneNumber}`;
  }

  if (
    country?.toLocaleLowerCase() === 'moneyhero' &&
    !normalizedPhoneNumber.startsWith('852')
  ) {
    return `852${normalizedPhoneNumber}`;
  }
  return normalizedPhoneNumber;
}
